import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, 
  Download,
  Users,
  Calendar,
  TrendingUp
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

// Using centralized api

function BookDownloads() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'agent') {
      fetchDownloadStats();
    }
  }, [user]);

  const fetchDownloadStats = async () => {
    try {
      const response = await api.get('/book-downloads/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching download stats:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'agent') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Acceso solo para agentes</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="book-downloads-page">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="h-8 w-8 text-amber-600" />
            <h1 className="text-4xl font-bold tracking-tight">Descargas del Libro</h1>
          </div>
          <p className="text-muted-foreground">
            Monitorea quién ha descargado "Marca Personal Deportiva"
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="bento-card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-700">Total Descargas</p>
                      <p className="text-4xl font-bold text-amber-900 mt-2">
                        {stats?.total_downloads || 0}
                      </p>
                    </div>
                    <div className="p-4 rounded-full bg-amber-200">
                      <Download className="h-8 w-8 text-amber-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bento-card bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-700">Usuarios Únicos</p>
                      <p className="text-4xl font-bold text-blue-900 mt-2">
                        {stats?.unique_users || 0}
                      </p>
                    </div>
                    <div className="p-4 rounded-full bg-blue-200">
                      <Users className="h-8 w-8 text-blue-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bento-card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-700">Últimos 7 días</p>
                      <p className="text-4xl font-bold text-green-900 mt-2">
                        {stats?.recent_downloads_7_days || 0}
                      </p>
                    </div>
                    <div className="p-4 rounded-full bg-green-200">
                      <TrendingUp className="h-8 w-8 text-green-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Downloads List */}
            <Card className="bento-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Historial de Descargas
                </CardTitle>
                <CardDescription>
                  Últimas descargas del libro "Marca Personal Deportiva"
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stats?.recent_downloads?.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">No hay descargas registradas aún</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {stats?.recent_downloads?.map((download) => (
                      <div 
                        key={download.download_id} 
                        className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-secondary/50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <span className="text-amber-700 font-semibold">
                              {download.user_name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{download.user_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(download.downloaded_at).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <Badge 
                          variant="secondary" 
                          className={download.user_role === 'player' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}
                        >
                          {download.user_role === 'player' ? 'Jugador' : 'Agente'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}

export default BookDownloads;
